import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { BrowserProvider } from 'ethers';
import { toast } from 'sonner';
import { api } from '../services/api';

const Web3Context = createContext(null);

// Polygon Amoy testnet config (matches backend app/services/blockchain.py NETWORKS.amoy)
export const POLYGON_AMOY = {
  chainId: 80002,
  chainIdHex: '0x13882',
  chainName: 'Polygon Amoy Testnet',
  nativeCurrency: { name: 'POL', symbol: 'POL', decimals: 18 },
  rpcUrls: ['https://rpc-amoy.polygon.technology/'],
  blockExplorerUrls: ['https://amoy.polygonscan.com'],
};

export const POLYGON_MAINNET = {
  chainId: 137,
  chainIdHex: '0x89',
  chainName: 'Polygon Mainnet',
  nativeCurrency: { name: 'POL', symbol: 'POL', decimals: 18 },
  rpcUrls: ['https://polygon-rpc.com'],
  blockExplorerUrls: ['https://polygonscan.com'],
};

export const useWeb3 = () => {
  const ctx = useContext(Web3Context);
  if (!ctx) throw new Error('useWeb3 must be inside Web3Provider');
  return ctx;
};

export const Web3Provider = ({ children }) => {
  const [walletAddress, setWalletAddress] = useState(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const [chainId, setChainId] = useState(null);
  const [provider, setProvider] = useState(null);

  // Check for saved wallet on mount
  useEffect(() => {
    const saved = localStorage.getItem('assagart_wallet');
    if (saved && window.ethereum) {
      setWalletAddress(saved);
      const p = new BrowserProvider(window.ethereum);
      setProvider(p);
      window.ethereum.request({ method: 'eth_chainId' })
        .then((id) => setChainId(parseInt(id, 16)))
        .catch(() => {});
    }
  }, []);

  // Listen for account/chain changes
  useEffect(() => {
    if (!window.ethereum) return;
    const handleAccounts = (accounts) => {
      if (accounts.length === 0) {
        disconnect();
      } else {
        setWalletAddress(accounts[0]);
        localStorage.setItem('assagart_wallet', accounts[0]);
      }
    };
    const handleChain = (id) => setChainId(parseInt(id, 16));

    window.ethereum.on('accountsChanged', handleAccounts);
    window.ethereum.on('chainChanged', handleChain);
    return () => {
      window.ethereum.removeListener('accountsChanged', handleAccounts);
      window.ethereum.removeListener('chainChanged', handleChain);
    };
  }, []);

  const connectWallet = useCallback(async () => {
    if (!window.ethereum) {
      const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
      if (isMobile) {
        const dappUrl = window.location.href.replace(/^https?:\/\//, '');
        window.location.href = `https://metamask.app.link/dapp/${dappUrl}`;
        return null;
      }
      window.open('https://chromewebstore.google.com/detail/metamask/nkbihfbeogaeaoehlefnkodbefgpgknn', '_blank');
      toast.error('MetaMask extension required. Opening the install page now.');
      return null;
    }

    setIsConnecting(true);
    try {
      const p = new BrowserProvider(window.ethereum);
      const accounts = await p.send('eth_requestAccounts', []);
      const address = accounts[0];
      const network = await p.getNetwork();

      setProvider(p);
      setWalletAddress(address);
      setChainId(Number(network.chainId));
      localStorage.setItem('assagart_wallet', address);

      const signer = await p.getSigner();
      const message = `Assagart Digital Twins: Link wallet ${address} at ${Date.now()}`;
      const signature = await signer.signMessage(message);

      try {
        await api.linkWallet({ wallet_address: address, signature, message });
      } catch (e) {
        console.warn('Backend wallet link failed, continuing with local connection');
      }

      toast.success(`Wallet connected: ${address.slice(0, 6)}...${address.slice(-4)}`);
      return address;
    } catch (err) {
      if (err.code === 4001) {
        toast.error('Connection rejected by user');
      } else {
        toast.error('Failed to connect wallet');
        console.error(err);
      }
      return null;
    } finally {
      setIsConnecting(false);
    }
  }, []);

  const disconnect = useCallback(() => {
    setWalletAddress(null);
    setProvider(null);
    setChainId(null);
    localStorage.removeItem('assagart_wallet');
    toast.success('Wallet disconnected');
  }, []);

  const switchToPolygon = useCallback(async (network = POLYGON_AMOY) => {
    if (!window.ethereum) return;
    try {
      await window.ethereum.request({
        method: 'wallet_switchEthereumChain',
        params: [{ chainId: network.chainIdHex }],
      });
    } catch (err) {
      if (err.code === 4902) {
        try {
          await window.ethereum.request({
            method: 'wallet_addEthereumChain',
            params: [{
              chainId: network.chainIdHex,
              chainName: network.chainName,
              nativeCurrency: network.nativeCurrency,
              rpcUrls: network.rpcUrls,
              blockExplorerUrls: network.blockExplorerUrls,
            }],
          });
        } catch (addErr) {
          toast.error(`Unable to add ${network.chainName} to MetaMask`);
        }
      } else if (err.code !== 4001) {
        toast.error('Network switch failed');
      }
    }
  }, []);

  const addDigitalTwinToMetaMask = useCallback(async ({ contractAddress, tokenId, name, imageUrl }) => {
    if (!window.ethereum) {
      toast.error('MetaMask is required to watch this asset');
      return false;
    }
    try {
      const added = await window.ethereum.request({
        method: 'wallet_watchAsset',
        params: {
          type: 'ERC1155',
          options: {
            address: contractAddress,
            tokenId: String(tokenId ?? 0),
            symbol: 'ASSAGART',
            image: imageUrl || undefined,
          },
        },
      });
      if (added) toast.success(`${name || 'Digital Twin'} added to MetaMask`);
      return !!added;
    } catch (err) {
      if (err.code === 4001) {
        // user rejected
        return false;
      }
      // MetaMask may not support ERC-1155 yet — fallback to opening Polygonscan
      toast.message('Your wallet does not support watching ERC-1155 tokens yet.');
      return false;
    }
  }, []);

  const isPolygonAmoy = chainId === POLYGON_AMOY.chainId;
  const isPolygonMainnet = chainId === POLYGON_MAINNET.chainId;
  const isPolygonNetwork = isPolygonAmoy || isPolygonMainnet;
  const shortAddress = walletAddress
    ? `${walletAddress.slice(0, 6)}...${walletAddress.slice(-4)}`
    : null;

  return (
    <Web3Context.Provider value={{
      walletAddress,
      shortAddress,
      isConnecting,
      chainId,
      isPolygonAmoy,
      isPolygonMainnet,
      isPolygonNetwork,
      provider,
      connectWallet,
      disconnect,
      switchToPolygon,
      addDigitalTwinToMetaMask,
      isMetaMaskInstalled: typeof window !== 'undefined' && !!window.ethereum,
    }}>
      {children}
    </Web3Context.Provider>
  );
};
